import {
  AnalyticsEventProperties,
  AnalyticsEventValue,
  FunnelInfoProperties,
  isExtendedAnalyticsEvent,
  isSnitcherEventName,
  PaymentFormCompletedProperties,
  PaymentFormStateProperties,
  snitcherContractProperties,
  SnitcherEventNames,
} from 'core/analytics/entities';
import { SnitcherService } from 'core/analytics/interfaces';
import { normalizeError } from 'core/common/errors';
import { HttpClient } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { isValidUuid } from 'core/common/utils/string';
import { FeatureFlags } from 'core/feature-flags/services';

export class HttpSnitcherService implements SnitcherService {
  private readonly publicEventUrl = '/snitcher/event';
  private readonly authorizedEventUrl = '/snitcher/event/authorized';
  private readonly accessTokenHeader = 'Access-Token';

  private isFunnelInfoTracked = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly logger: Logger,
    private readonly featureFlags: FeatureFlags,
  ) {}

  async trackEvent(
    event: AnalyticsEventValue,
    properties: AnalyticsEventProperties = {},
  ): Promise<void> {
    if (isExtendedAnalyticsEvent(event)) {
      return this.sendEvent(event.name, { ...event.properties, ...properties });
    }

    return this.sendEvent(event, properties);
  }

  trackPaymentFormState(
    properties: PaymentFormStateProperties & AnalyticsEventProperties,
  ): Promise<void> {
    return this.sendEvent(SnitcherEventNames.PAYMENT_FORM_STATE, properties);
  }

  paymentFormCompleted(properties: PaymentFormCompletedProperties): Promise<void> {
    return this.sendEvent(SnitcherEventNames.PAYMENT_FORM_COMPLETED, properties);
  }

  trackFunnelInfo(properties: FunnelInfoProperties): Promise<void> {
    if (this.isFunnelInfoTracked) return Promise.resolve();

    this.isFunnelInfoTracked = true;

    return this.sendEvent(SnitcherEventNames.FUNNEL_INFO, properties);
  }

  private async sendEvent(eventName: string, properties: AnalyticsEventProperties): Promise<void> {
    if (!isSnitcherEventName(eventName)) return;

    const url = this.isAuthorized() ? this.authorizedEventUrl : this.publicEventUrl;
    const splitId = this.featureFlags.getSplitId();

    try {
      await this.httpClient.post(url, {
        data: {
          event_name: eventName,
          event_time: Date.now(),
          ...(isValidUuid(splitId) ? { split_id: splitId } : {}),
          properties: this.pickContractProperties(eventName, properties),
        },
      });
    } catch (err) {
      this.logger.error(normalizeError(err));
    }
  }

  private pickContractProperties(
    eventName: SnitcherEventNames,
    properties: AnalyticsEventProperties,
  ): AnalyticsEventProperties {
    const allowedKeys = snitcherContractProperties[eventName];
    const contractEntries = Object.entries(properties).filter(([key]) => allowedKeys.includes(key));
    const contractEventProperties = Object.fromEntries(contractEntries);

    return contractEventProperties;
  }

  private isAuthorized(): boolean {
    const headers = this.httpClient.getHeaders();

    return !!headers[this.accessTokenHeader];
  }
}
